import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container, Typography, List, ListItem, ListItemText, Box, Button, TextField, IconButton, MenuItem, Stack, Divider, Switch, Chip, InputAdornment } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Close as CloseIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dataAnalysisTopicsService, { DataAnalysisTopic } from '../../services/dataAnalysisTopicsService';
import { usePageTitle } from '../../hooks/usePageTitle';

const DataAnalysisTopics: React.FC = () => {
  usePageTitle('Data Analysis Topics');
  
  const navigate = useNavigate();
  const [items, setItems] = useState<DataAnalysisTopic[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<Partial<DataAnalysisTopic>>({});
  const [q, setQ] = useState<string>('');
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const load = async (query?: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await dataAnalysisTopicsService.list(query);
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (editingId && firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, [editingId]);

  const startEdit = (it: DataAnalysisTopic) => {
    setEditingId(it._id);
    setForm({ ...it });
  };

  const startCreate = () => {
    setEditingId('new');
    setForm({ name: '', category: 'Other', description: '', order: (items?.length || 0) + 1, isActive: true } as Partial<DataAnalysisTopic>);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({});
  };

  const save = async () => {
    try {
      if (editingId === 'new') {
        const created = await dataAnalysisTopicsService.create({
          name: form.name || '',
          category: (form.category as DataAnalysisTopic['category']) || 'Other',
          description: form.description || undefined,
          order: typeof form.order === 'number' ? form.order : undefined,
          isActive: typeof form.isActive === 'boolean' ? form.isActive : undefined,
        });
        setItems((prev) => [created, ...prev]);
      } else if (editingId) {
        const updated = await dataAnalysisTopicsService.update(editingId, {
          name: form.name,
          category: form.category as DataAnalysisTopic['category'],
          description: form.description,
          order: form.order,
          isActive: form.isActive,
        });
        setItems((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
      }
      cancelEdit();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Save failed');
    }
  };

  const remove = async (id: string) => {
    try {
      await dataAnalysisTopicsService.remove(id);
      setItems((prev) => prev.filter((p) => p._id !== id));
      if (editingId === id) cancelEdit();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Delete failed');
    }
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(items)) return [] as DataAnalysisTopic[];
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((i) =>
      [i.name, i.category, i.description]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [items, q]);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          onClick={() => navigate('/dhs')}
          startIcon={<ArrowBackIcon />}
          aria-label="Back to DHS Study"
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search topics"
          aria-label="Search topics"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            )
          }}
        />
        <Button
          onClick={startCreate}
          startIcon={<AddIcon />}
          aria-label="Add topic"
          sx={{ ml: 2 }}
        >
          Add
        </Button>
      </Box>

      <Typography variant="h4" component="h1" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
        Data Analysis Topics
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Important topics for DHS data analysis covering governance, privacy, quality, interoperability, and more.
      </Typography>

      {error && (
        <Typography role="alert" aria-live="assertive" sx={{ color: 'error.main', mb: 2 }}>{error}</Typography>
      )}

      <List aria-label="Data analysis topics list">
        {editingId === 'new' && (
          <ListItem divider sx={{ bgcolor: 'background.default' }}>
            <Stack spacing={1} sx={{ width: '100%' }}>
              <TextField
                inputRef={firstFieldRef}
                label="Name"
                fullWidth
                value={form.name || ''}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  label="Category"
                  sx={{ width: 220 }}
                  value={form.category || 'Other'}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as DataAnalysisTopic['category'] }))}
                >
                  <MenuItem value="Governance">Governance</MenuItem>
                  <MenuItem value="Privacy">Privacy</MenuItem>
                  <MenuItem value="Security">Security</MenuItem>
                  <MenuItem value="Quality">Quality</MenuItem>
                  <MenuItem value="Technical">Technical</MenuItem>
                  <MenuItem value="Operational">Operational</MenuItem>
                  <MenuItem value="Compliance">Compliance</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
                <TextField
                  label="Order"
                  type="number"
                  sx={{ width: 120 }}
                  value={form.order ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))}
                />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1 }}>
                  <Typography component="span">Active</Typography>
                  <Switch
                    checked={!!form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    inputProps={{ 'aria-label': 'Active' }}
                  />
                </Stack>
              </Stack>
              <TextField
                label="Description"
                multiline
                minRows={2}
                value={form.description || ''}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
              <Stack direction="row" spacing={1}>
                <Button onClick={save} startIcon={<SaveIcon />} aria-label="Save new topic" variant="contained">Save</Button>
                <Button onClick={cancelEdit} startIcon={<CloseIcon />} aria-label="Cancel" color="inherit">Cancel</Button>
              </Stack>
            </Stack>
          </ListItem>
        )}

        {(filtered || []).map((it) => (
          <ListItem key={it._id} divider>
            {editingId === it._id ? (
              <Stack spacing={1} sx={{ width: '100%' }}>
                <TextField
                  inputRef={firstFieldRef}
                  label="Name"
                  fullWidth
                  value={form.name || ''}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    select
                    label="Category"
                    sx={{ width: 220 }}
                    value={(form.category as DataAnalysisTopic['category']) || 'Other'}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as DataAnalysisTopic['category'] }))}
                  >
                    <MenuItem value="Governance">Governance</MenuItem>
                    <MenuItem value="Privacy">Privacy</MenuItem>
                    <MenuItem value="Security">Security</MenuItem>
                    <MenuItem value="Quality">Quality</MenuItem>
                    <MenuItem value="Technical">Technical</MenuItem>
                    <MenuItem value="Operational">Operational</MenuItem>
                    <MenuItem value="Compliance">Compliance</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                  <TextField
                    label="Order"
                    type="number"
                    sx={{ width: 120 }}
                    value={form.order ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))}
                  />
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1 }}>
                    <Typography component="span">Active</Typography>
                    <Switch
                      checked={!!form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      inputProps={{ 'aria-label': 'Active' }}
                    />
                  </Stack>
                </Stack>
                <TextField
                  label="Description"
                  multiline
                  minRows={2}
                  value={form.description || ''}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
                <Stack direction="row" spacing={1}>
                  <Button onClick={save} startIcon={<SaveIcon />} aria-label={`Save ${it.name}`} variant="contained">Save</Button>
                  <Button onClick={cancelEdit} startIcon={<CloseIcon />} aria-label="Cancel" color="inherit">Cancel</Button>
                  <Button onClick={() => remove(it._id)} startIcon={<DeleteIcon />} aria-label={`Delete ${it.name}`} color="error">Delete</Button>
                </Stack>
              </Stack>
            ) : (
              <Box
                role="button"
                tabIndex={0}
                aria-label={`Edit ${it.name}`}
                onClick={() => startEdit(it)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEdit(it); } }}
                style={{ width: '100%', outline: 'none' }}
              >
                <Stack spacing={0.5}>
                  <ListItemText primaryTypographyProps={{ sx: { fontWeight: 700 } }} primary={it.name} />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" color="primary" label={it.category} />
                    {typeof it.order === 'number' && <Chip size="small" label={`Order ${it.order}`} />}
                    <Chip size="small" label={it.isActive ? 'Active' : 'Inactive'} color={it.isActive ? 'success' : 'default'} />
                  </Stack>
                  {it.description && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{it.description}</Typography>
                  )}
                </Stack>
              </Box>
            )}
          </ListItem>
        ))}
        {loading && (
          <ListItem>
            <ListItemText primary="Loading..." />
          </ListItem>
        )}
        {!loading && filtered.length === 0 && (
          <ListItem>
            <ListItemText primary="No topics found" />
          </ListItem>
        )}
      </List>
    </Container>
  );
};

export default DataAnalysisTopics;
